import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';

import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';

import IconButton from '@material-ui/core/IconButton';
import TypeAheadHandler from '../../helpers/TypeAheadHandler';

interface IProps {
    filter: string;
    onFilter: (filter: string) => void;
    onChange?: (filter: string) => void;
    onReset?: () => void;
    omitEmpty?: boolean;
    placeholder: string;
    className?: string;
    shouldFocus?: boolean;
    onRef?: (node: any) => void;
    icon?: React.ComponentType<void>;
    autoFocus?: boolean;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
}

const PhraseFilter: React.FC<IProps> = ({
    filter: filterProp,
    onFilter,
    onChange,
    onReset,
    omitEmpty,
    placeholder,
    shouldFocus,
    onRef,
    icon,
    autoFocus,
    error,
    helperText,
    disabled,
}) => {
    const [filter, setFilter] = useState(filterProp);
    const textInput = useRef<any>(null);
    const isLocalChange = useRef(false);
    const latestProps = useRef({ onFilter, onReset, omitEmpty });
    latestProps.current = { onFilter, onReset, omitEmpty };

    const typeAheadHandler = useRef(
        new TypeAheadHandler(
            1,
            (value: string) => {
                const {
                    onFilter: latestOnFilter,
                    onReset: latestOnReset,
                    omitEmpty: latestOmitEmpty,
                } = latestProps.current;
                if (!latestOmitEmpty || !!value) {
                    latestOnFilter(value);
                } else if (latestOnReset) {
                    latestOnReset();
                }
            },
            filterProp
        )
    );

    const focusInput = useCallback(() => {
        if (textInput.current) {
            textInput.current.focus();
        }
    }, []);

    useEffect(() => {
        if (shouldFocus) {
            focusInput();
        }
    }, [shouldFocus, focusInput]);

    useEffect(() => {
        if (isLocalChange.current) {
            isLocalChange.current = false;
            return;
        }
        setFilter(filterProp);
    }, [filterProp]);

    const resetFilter = useCallback(() => {
        if (onReset) {
            onReset();
        }
        setFilter('');
        focusInput();
    }, [onReset, focusInput]);

    const handleChange = useCallback(
        ({ target: { value: newFilter } }: ChangeEvent<HTMLInputElement>) => {
            isLocalChange.current = true;
            typeAheadHandler.current.change(newFilter);
            if (onChange) {
                onChange(newFilter);
            } else {
                setFilter(newFilter);
            }
        },
        [onChange]
    );

    return (
        <TextField
            type="text"
            placeholder={placeholder}
            onChange={handleChange}
            value={onChange ? filterProp : filter}
            margin="normal"
            autoFocus={autoFocus}
            error={error}
            helperText={helperText}
            inputRef={(input) => {
                textInput.current = input;
                if (onRef) {
                    onRef(input);
                }
            }}
            fullWidth={true}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <IconButton color="primary" disabled={disabled}>
                            {icon || <SearchIcon />}
                        </IconButton>
                    </InputAdornment>
                ),
                endAdornment: onReset && (
                    <InputAdornment position="end">
                        <IconButton
                            color="primary"
                            onClick={resetFilter}
                            disabled={disabled}
                        >
                            <ClearIcon />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            disabled={disabled}
        />
    );
};

export default PhraseFilter;
